/**
 * PlanTask Component
 * 
 * React component for the PlanTask TipTap node.
 * Renders a task card within the document editor showing:
 * - Order number and description (human-readable)
 * - Status badge (pending/in_progress/completed/failed)
 * - Target document name if present
 * 
 * Machine-readable fields (operation, targetDocumentId) are stored
 * but not displayed in the UI.
 */

import React from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';

// Status colors matching the design system
const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Pending' },
  in_progress: { bg: 'bg-warning-light', text: 'text-warning', label: 'In Progress' },
  completed: { bg: 'bg-success-light', text: 'text-success', label: 'Completed' },
  skipped: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Skipped' },
  failed: { bg: 'bg-destructive-light', text: 'text-destructive', label: 'Failed' },
};

// Status icons
const statusIcons: Record<string, string> = {
  pending: '○',
  in_progress: '◐',
  completed: '●',
  skipped: '◌',
  failed: '✕',
};

export const PlanTaskComponent: React.FC<NodeViewProps> = ({
  node,
  selected,
}) => {
  const description = node.attrs.description || 'No description';
  const status = node.attrs.status || 'pending';
  const order = node.attrs.order || 0;
  const targetDocumentName = node.attrs.targetDocumentName;
  const result = node.attrs.result;
  const error = node.attrs.error;

  const statusStyle = statusConfig[status] || statusConfig.pending;
  const statusIcon = statusIcons[status] || statusIcons.pending;

  return (
    <NodeViewWrapper className="plan-task-wrapper my-2">
      <div
        className={`
          flex items-start gap-3 p-3 rounded-lg border
          ${selected ? 'ring-2 ring-primary border-primary' : 'border-border'}
          ${status === 'completed' ? 'bg-success-light/30' : ''}
          ${status === 'failed' ? 'bg-destructive-light/30' : ''}
          ${status === 'in_progress' ? 'bg-warning-light/30' : ''}
          ${status === 'pending' || status === 'skipped' ? 'bg-muted/30' : ''}
          transition-all
        `}
      >
        {/* Order number */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <span className="text-sm font-semibold text-primary">
            {order > 0 ? order : '—'}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Description */}
          <p className="text-sm font-medium text-foreground mb-1">
            {description}
          </p>

          {/* Target document */}
          {targetDocumentName && (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {targetDocumentName}
            </p>
          )}

          {/* Result (if completed) */}
          {status === 'completed' && result && (
            <p className="text-xs text-success mt-1">
              {result}
            </p>
          )}

          {/* Error (if failed) */}
          {status === 'failed' && error && (
            <p className="text-xs text-destructive mt-1">
              {error}
            </p>
          )}
        </div>

        {/* Status badge */}
        <div className={`flex-shrink-0 px-2 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
          <span className="mr-1">{statusIcon}</span>
          {statusStyle.label}
        </div>
      </div>
    </NodeViewWrapper>
  );
};
